import React, { useState, useEffect } from 'react';
import { UserTickerList } from '../../services/userTickerListApi';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getTextSizeClass, getSubsectionHeadingSizeClass } from '../../utils/fontSizeUtils';

interface NewListDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateList: (listName: string) => Promise<void>;
  existingLists: UserTickerList[];
}

const NewListDialog: React.FC<NewListDialogProps> = ({
  isOpen,
  onClose,
  onCreateList,
  existingLists
}) => {
  const { fontSize } = useFontSize();
  const [listName, setListName] = useState('');
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setListName('');
      setError('');
    }
  }, [isOpen]);

  // Check for duplicate names
  const isDuplicateName = (name: string) => {
    return existingLists.some(list => 
      list.list_name.toLowerCase() === name.toLowerCase().trim()
    );
  };

  // Validate list name
  const validateListName = (name: string) => {
    const trimmedName = name.trim();
    
    if (!trimmedName) {
      return 'List name cannot be empty';
    }
    
    if (trimmedName.length > 50) {
      return 'List name must be 50 characters or less';
    }
    
    if (isDuplicateName(trimmedName)) {
      return 'A list with this name already exists';
    }
    
    return '';
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedName = listName.trim();
    const validationError = validateListName(trimmedName);
    
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setIsCreating(true);
    setError('');
    
    try {
      await onCreateList(trimmedName);
      onClose();
    } catch (err) {
      setError('Failed to create list. Please try again.');
      console.error('Error creating list:', err);
    } finally {
      setIsCreating(false);
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setListName(value);
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  // Handle escape key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999]">
      <div 
        className="bg-white dark:bg-background-card rounded-lg shadow-xl max-w-md w-full mx-4 z-[10000]"
        onKeyDown={handleKeyDown}
      >
        <div className="p-6">
          <h2 className={`${getSubsectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-dark-primary mb-4`}>
            Create New List
          </h2>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label 
                htmlFor="listName" 
                className={`block ${getTextSizeClass(fontSize)} font-medium text-text-light-secondary dark:text-text-dark-secondary mb-2`}
              >
                List Name
              </label>
              <input
                id="listName"
                type="text"
                value={listName}
                onChange={handleInputChange}
                placeholder="Enter list name..."
                className="w-full px-3 py-2 border border-border-light dark:border-border-color rounded-md bg-white dark:bg-background-card text-text-light-primary dark:text-text-dark-primary placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                autoFocus
                maxLength={50}
                disabled={isCreating}
              />
              {error && (
                <p className={`mt-2 ${getTextSizeClass(fontSize)} text-red-600 dark:text-red-400`}>
                  {error}
                </p>
              )}
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className={`px-4 py-2 ${getTextSizeClass(fontSize)} font-medium text-text-light-secondary dark:text-text-dark-secondary hover:text-text-light-primary dark:hover:text-text-dark-primary transition-colors`}
                disabled={isCreating}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isCreating || !listName.trim()}
                className={`px-4 py-2 bg-primary text-gray-900 ${getTextSizeClass(fontSize)} font-medium rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isCreating ? 'Creating...' : 'Create List'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewListDialog;
